import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Images.css";

const Images = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const sentinelRef = useRef(null);

  // ─── Core state ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("all");
  const [folders, setFolders] = useState([]);
  const [openFolderId, setOpenFolderId] = useState(null);
  const [openFolderName, setOpenFolderName] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [fullscreenImg, setFullscreenImg] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_LIMIT = 30;

  // Storage
  const [storageInfo, setStorageInfo] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(true);

  // Create folder
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderPassword, setNewFolderPassword] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Upload
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalUploadCount, setTotalUploadCount] = useState(0);
  const [uploadProgressPct, setUploadProgressPct] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  // Download modal
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFolderId, setDownloadFolderId] = useState(null);
  const [downloadPassword, setDownloadPassword] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState("");

  // Payment
  const [offers, setOffers] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);

  // ─── Auth & initial load ──────────────────────────────────────────────────
  const handleWheel = (e) => {
    if (!fullscreenImg) return;
    if (e.deltaY < 0) setZoomLevel(z => Math.min(z + 0.1, 4));
    else setZoomLevel(z => Math.max(z - 0.1, 0.5));
  };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoadingStorage(true);
      const [infoRes, foldersRes, offersRes] = await Promise.all([
        axios.get("/api/images/storage-info", { headers }),
        axios.get("/api/images/folders", { headers }),
        axios.get("/api/payment/offers"),
      ]);
      if (infoRes.data.success) setStorageInfo(infoRes.data);
      if (foldersRes.data.success) setFolders(foldersRes.data.folders);
      if (offersRes.data.success) {
        setOffers(offersRes.data.offers.filter(o => o.title && o.title.toLowerCase().includes("image")));
      }
    } catch (err) { console.error(err); }
    finally { setLoadingStorage(false); }
  };

  const fetchFolders = async () => {
    try {
      const res = await axios.get("/api/images/folders", { headers });
      if (res.data.success) setFolders(res.data.folders);
    } catch (err) { console.error(err); }
  };

  const fetchImages = useCallback(async (folderId, tab, page = 1) => {
    try {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      const endpoint = tab === "selected" ? "/api/images/selected" : "/api/images/all";
      const res = await axios.get(endpoint, { headers, params: { folderId, page, limit: PAGE_LIMIT } });
      if (res.data.success) {
        const newImgs = res.data.images;
        setImages(prev => page === 1 ? newImgs : [...prev, ...newImgs]);
        setHasMore(res.data.pagination?.hasMore ?? false);
        setCurrentPage(page);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [headers]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        fetchImages(openFolderId, activeTab, currentPage + 1);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, openFolderId, activeTab, currentPage, fetchImages]);

  useEffect(() => {
    if (openFolderId) fetchImages(openFolderId, activeTab, 1);
  }, [activeTab, openFolderId]);

  // ─── Storage check ──────────────────────────────────────────────────────────
  const storagePurchased = storageInfo && storageInfo.imageStorageLimit > 0;
  const formatBytes = (b) => {
    if (!b) return "0 B";
    if (b >= 1024 * 1024 * 1024) return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    return `${(b / 1024).toFixed(0)} KB`;
  };
  const formatMB = formatBytes;

  // ─── Folder actions ─────────────────────────────────────────────────────────
  const openFolder = (f) => { setOpenFolderId(f._id); setOpenFolderName(f.name); setImages([]); };
  const closeFolder = () => { setOpenFolderId(null); setOpenFolderName(""); setImages([]); };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim() || !newFolderPassword.trim()) { alert("Folder name and password are required."); return; }
    try {
      setCreatingFolder(true);
      const res = await axios.post("/api/images/create-folder", { name: newFolderName.trim(), password: newFolderPassword.trim() }, { headers });
      if (res.data.success) {
        setShowCreateFolder(false); setNewFolderName(""); setNewFolderPassword("");
        fetchFolders(); loadAll();
      }
    } catch (err) { alert(err.response?.data?.error || "Failed to create folder"); }
    finally { setCreatingFolder(false); }
  };

  // ─── Selection & Deletion ──────────────────────────────────────────────────
  const toggleSelect = async (img) => {
    const newSel = !img.selected;
    const ok = window.confirm(newSel ? "Select this image?" : "Remove from selected?");
    if (!ok) return;
    try {
      await axios.patch(`/api/images/${img._id}/select`, { selected: newSel }, { headers });
      setImages(prev => {
        let updated = prev.map(i => i._id === img._id ? { ...i, selected: newSel } : i);
        if (activeTab === "selected" && !newSel) updated = updated.filter(i => i._id !== img._id);
        return updated;
      });
      fetchFolders();
    } catch (err) { alert("Failed to update selection."); }
  };

  const handleDelete = async (imgId, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to permanently delete this image?")) return;
    try {
      const res = await axios.delete(`/api/images/${imgId}`, { headers });
      if (res.data.success) {
        setImages(prev => prev.filter(i => i._id !== imgId));
        fetchFolders(); loadAll();
      }
    } catch (err) { alert("Failed to delete image."); }
  };

  // ─── File selection & drag-drop ─────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const imgs = Array.from(e.target.files).filter(f => f.type.startsWith("image/"));
    if (imgs.length === 0) { alert("No image files found."); return; }
    if (imgs.length > 500) { alert("Max 500 images allowed. Only first 500 selected."); imgs.splice(500); }
    setSelectedFiles(imgs);
  };

  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const items = e.dataTransfer.items;
    const imageFiles = [];

    const readEntry = (entry) => new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file) => { if (file.type.startsWith("image/")) imageFiles.push(file); resolve(); });
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        reader.readEntries(async (entries) => {
          for (const en of entries) await readEntry(en);
          resolve();
        });
      } else { resolve(); }
    });

    for (const item of items) {
      const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
      if (entry) await readEntry(entry);
    }
    if (imageFiles.length === 0) { alert("No image files found."); return; }
    if (imageFiles.length > 500) { alert(`Found ${imageFiles.length} images. Max 500, trimming.`); imageFiles.splice(500); }
    setSelectedFiles(imageFiles);
  };

  // ─── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (selectedFiles.length === 0) { alert("Select images first."); return; }
    setUploading(true); setUploadedCount(0); setUploadProgressPct(0); setTotalUploadCount(selectedFiles.length);
    const batch = 10;
    let done = 0;
    try {
      for (let i = 0; i < selectedFiles.length; i += batch) {
        const chunk = selectedFiles.slice(i, i + batch);
        const fd = new FormData();
        fd.append("folderId", openFolderId);
        chunk.forEach(f => fd.append("images", f));
        
        await axios.post("/api/images/upload", fd, { 
          headers: { ...headers, "Content-Type": "multipart/form-data" }, 
          timeout: 300000,
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const overallPct = ((done + (chunk.length * (pct / 100))) / selectedFiles.length) * 100;
            setUploadProgressPct(overallPct);
          }
        });
        done += chunk.length;
        setUploadedCount(done);
        setUploadProgressPct((done / selectedFiles.length) * 100);
      }
      alert(`✅ Uploaded ${done} images!`);
      setSelectedFiles([]); loadAll();
      if (openFolderId) fetchImages(openFolderId, activeTab);
    } catch (err) {
      const msg = err.response?.data?.error || "Upload failed.";
      if (msg === "IMAGE_STORAGE_NOT_PURCHASED") alert("Please purchase image storage first.");
      else alert(msg);
    }
    finally { setUploading(false); }
  };

  // ─── Download ───────────────────────────────────────────────────────────────
  const handleFolderDownload = async (e) => {
    e.preventDefault();
    if (!downloadPassword.trim()) { alert("Password required."); return; }
    try {
      setDownloading(true);
      setDownloadProgress("Generating ZIP...");
      
      const res = await axios.post(
        "/api/images/download-folder", 
        { folderId: downloadFolderId, password: downloadPassword.trim() }, 
        { headers, responseType: "blob", timeout: 600000 } // 10 min timeout for large zips
      );
      
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      
      // Try to get filename from header
      const contentDisposition = res.headers['content-disposition'];
      let filename = "folder_download.zip";
      if (contentDisposition) {
        const parts = contentDisposition.split('filename=');
        if (parts.length === 2) filename = parts[1].replace(/['"]/g, '');
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      
      setShowDownloadModal(false);
      setDownloadProgress("");
      
    } catch (err) { 
      let msg = "Download failed. Check password.";
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try { msg = JSON.parse(text).error || msg; } catch (e) {}
      } else {
        msg = err.response?.data?.error || msg;
      }
      alert(msg); 
    }
    finally { setDownloading(false); setDownloadProgress(""); }
  };

  // ─── Payment ────────────────────────────────────────────────────────────────
  const handlePayment = async (offer) => {
    if (!window.Razorpay) { alert("Razorpay SDK not loaded. Refresh page."); return; }
    try {
      setProcessingPayment(true);
      const orderRes = await axios.post("/api/payment/create-order", { offerId: offer._id }, { headers });
      const { order, key } = orderRes.data;
      const rzp = new window.Razorpay({
        key, amount: order.amount, currency: order.currency,
        name: "Golden Memory", description: offer.description, order_id: order.id,
        handler: async (response) => {
          try {
            const v = await axios.post("/api/payment/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { headers });
            if (v.data.success) { alert("🎉 Payment successful! Image storage unlocked!"); loadAll(); }
          } catch { alert("Payment verification failed."); }
          finally { setProcessingPayment(false); }
        },
        theme: { color: "#667eea" },
        modal: { ondismiss: () => setProcessingPayment(false) },
      });
      rzp.open();
    } catch { alert("Payment failed."); setProcessingPayment(false); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  if (loadingStorage) {
    return <div className="img-page"><div className="img-loader"><div className="img-spinner" /><p>Loading...</p></div></div>;
  }

  return (
    <div className="img-page">
      {/* ──── Header ───────────────────────────────────────── */}
      <div className="img-page-header">
        <h2>📷 Image Editor</h2>
        <div className="img-tabs">
          <button className={`img-tab ${activeTab === "all" ? "active" : ""}`} onClick={() => { setActiveTab("all"); if (!openFolderId) fetchFolders(); }}>All</button>
          <button className={`img-tab ${activeTab === "selected" ? "active" : ""}`} onClick={() => { setActiveTab("selected"); if (!openFolderId) fetchFolders(); }}>Selected</button>
        </div>
      </div>

      {/* ──── Storage Bar ──────────────────────────────────── */}
      {storagePurchased && storageInfo && (
        <div className="img-storage-card">
          <div className="img-storage-row">
            <span>☁️ Storage</span>
            <span className="img-storage-nums">{formatMB(storageInfo.usedImageStorage)} / {formatMB(storageInfo.imageStorageLimit)}</span>
          </div>
          <div className="img-storage-bar-bg">
            <div className="img-storage-bar-fill" style={{ width: `${Math.min((storageInfo.usedImageStorage / storageInfo.imageStorageLimit) * 100, 100)}%` }} />
          </div>
          <div className="img-storage-row sub">
            <span>{storageInfo.imageCount} images stored</span>
            <span>{formatMB(storageInfo.imageStorageLimit - storageInfo.usedImageStorage)} free</span>
          </div>
        </div>
      )}

      {/* ──── Payment Gate (if no storage) ──────────────────── */}
      {!storagePurchased && (
        <div className="img-payment-section">
          <div className="img-payment-glass">
            <div className="img-payment-glow" />
            <h3>🔓 Unlock Image Storage</h3>
            <p>Purchase 8 GB of lifetime cloud storage to upload, organise, and securely download your photos.</p>
            {offers.map(offer => {
              const discountPct = offer.originalPrice && offer.originalPrice > offer.price
                ? Math.round(100 - (offer.price / offer.originalPrice) * 100)
                : null;
              return (
                <div key={offer._id} className="img-offer-card">
                  <div className="img-offer-top">
                    <h4>{offer.title}</h4>
                    {discountPct && <span className="img-offer-badge">{discountPct}% OFF</span>}
                  </div>
                  <p className="img-offer-desc">{offer.description}</p>
                  {offer.features && <ul className="img-offer-features">{offer.features.map((f, i) => <li key={i}>✓ {f}</li>)}</ul>}
                  <div className="img-offer-price-row">
                    {offer.originalPrice && <span className="img-offer-old">₹{offer.originalPrice.toLocaleString()}</span>}
                    <span className="img-offer-new">₹{offer.price}</span>
                    <span className="img-offer-period">Lifetime</span>
                  </div>
                  <button className="img-pay-btn" onClick={() => handlePayment(offer)} disabled={processingPayment}>
                    {processingPayment ? "Processing…" : `💳 Pay ₹${offer.price}`}
                  </button>
                </div>
              );
            })}
            {offers.length === 0 && <p className="img-no-offer">Image offer not found. Run seed-offer first.</p>}
          </div>
        </div>
      )}

      {/* Upload was removed from here and moved inside the folder view */}

      {/* ──── Breadcrumb ───────────────────────────────────── */}
      {openFolderId && (
        <div className="img-breadcrumb">
          <button onClick={closeFolder}>← Folders</button>
          <span>{openFolderName}</span>
          {activeTab === "selected" && (
            <button className="img-breadcrumb-dl" onClick={() => { setDownloadFolderId(openFolderId); setDownloadPassword(""); setShowDownloadModal(true); }}>
              ⬇️ Download Folder
            </button>
          )}
        </div>
      )}

      {/* ──── Content: Folders or Images ───────────────────── */}
      <div className="img-content">
        {loading ? (
          <div className="img-loader"><div className="img-spinner" /><p>Loading…</p></div>
        ) : openFolderId ? (
          <>
            {/* Upload Section rendered only inside a folder when All is selected */}
            {storagePurchased && activeTab === "all" && (
              <div className="img-upload-section inside-folder">
                {/* Dropzone */}
                <div
                  className={`img-dropzone ${dragActive ? "drag-active" : ""}`}
                  onDragEnter={() => setDragActive(true)}
                  onDragLeave={() => setDragActive(false)}
                  onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={handleDrop}
                >
                  <input type="file" accept="image/*" multiple ref={fileInputRef} onChange={handleFileSelect} style={{ display: "none" }} />
                  <input type="file" accept="image/*" multiple ref={folderInputRef} onChange={handleFileSelect} style={{ display: "none" }} webkitdirectory="" directory="" />
                  <div className="img-dropzone-inner">
                    <span className="img-dropzone-icon">📁</span>
                    <h4>Drag & drop images here</h4>
                    <p>Or click to browse</p>
                    <div className="img-dropzone-btns">
                      <button onClick={() => fileInputRef.current?.click()}>Select Images</button>
                    </div>
                  </div>
                </div>

                {/* Selected files preview */}
                {selectedFiles.length > 0 && !uploading && (
                  <div className="img-selected-bar">
                    <span>🖼️ {selectedFiles.length} image{selectedFiles.length > 1 ? "s" : ""} ready</span>
                    <div className="img-selected-actions">
                      <button className="img-btn-upload" onClick={handleUpload}>Upload {selectedFiles.length}</button>
                      <button className="img-btn-clear" onClick={() => setSelectedFiles([])}>Clear</button>
                    </div>
                  </div>
                )}

                {/* Upload progress */}
                {uploading && (
                  <div className="img-upload-progress">
                    <div className="img-upload-progress-label">Uploading... {Math.round(uploadProgressPct)}% ({uploadedCount} / {totalUploadCount} overall)</div>
                    <div className="img-storage-bar-bg large">
                      <div className="img-storage-bar-fill" style={{ width: `${uploadProgressPct}%` }} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {images.length > 0 ? (
              <>
                <div className="img-grid">
                  {images.map(img => (
                    <div key={img._id} className={`img-card ${img.selected ? "selected" : ""}`}>
                      <div
                        className="img-card-img"
                        onClick={() => { setFullscreenImg(img); setZoomLevel(1); }}
                        onContextMenu={e => e.preventDefault()}
                        style={{ userSelect: 'none' }}
                      >
                        <img
                          src={img.url}
                          alt={img.originalName}
                          loading="lazy"
                          draggable={false}
                          onContextMenu={e => e.preventDefault()}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        />
                        {img.selected && <div className="img-check-badge">✓</div>}
                        {/* transparent overlay to block right-click save */}
                        <div className="img-protect-overlay" onContextMenu={e => e.preventDefault()} />
                      </div>
                      <div className="img-card-actions">
                        <label className="img-card-check" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={img.selected} onChange={() => toggleSelect(img)} />
                          <span>{img.selected ? "Selected" : "Select"}</span>
                        </label>
                        {activeTab === "all" && (
                          <button className="img-card-delete" onClick={(e) => handleDelete(img._id, e)}>🗑️</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="img-sentinel">
                  {loadingMore && <div className="img-loader-inline"><div className="img-spinner" /></div>}
                </div>
              </>
            ) : <div className="img-empty"><p>No {activeTab === "selected" ? "selected " : ""}images in this folder yet.</p></div>}
          </>
        ) : (
          folders.length > 0 ? (
            <div className="img-folders">
              {folders.map(f => (
                <div key={f._id} className="img-folder" onClick={() => openFolder(f)}>
                  <div className="img-folder-icon-wrap"><span>📁</span></div>
                  <div className="img-folder-meta">
                    <strong>{f.name}</strong>
                    <small>{f.imageCount} imgs · {f.selectedCount} selected</small>
                  </div>
                  {activeTab === "selected" && (
                    <button className="img-folder-dl" onClick={e => { e.stopPropagation(); setDownloadFolderId(f._id); setDownloadPassword(""); setShowDownloadModal(true); }}>⬇️</button>
                  )}
                </div>
              ))}
              <div className="img-folder add" onClick={() => setShowCreateFolder(true)}>
                <div className="img-folder-icon-wrap"><span>➕</span></div>
                <div className="img-folder-meta"><strong>New Folder</strong></div>
              </div>
            </div>
          ) : (
            <div className="img-empty">
              <p>No folders yet.</p>
              <button className="img-btn-upload" onClick={() => setShowCreateFolder(true)}>Create First Folder</button>
            </div>
          )
        )}
      </div>

      {/* ──── Fullscreen ───────────────────────────────────── */}
      {fullscreenImg && (
        <div className="img-fs-overlay" onClick={() => setFullscreenImg(null)}>
          <div className="img-fs-wrap" onClick={e => e.stopPropagation()} onWheel={handleWheel}>
            <button className="img-fs-close" onClick={() => setFullscreenImg(null)}>✕</button>
            <div className="img-fs-zoom-controls">
              <button onClick={() => setZoomLevel(z => Math.min(z + 0.25, 4))}>+</button>
              <span>{Math.round(zoomLevel * 100)}%</span>
              <button onClick={() => setZoomLevel(z => Math.max(z - 0.25, 0.5))}>-</button>
              <button onClick={() => setZoomLevel(1)}>Reset</button>
            </div>
            <div className="img-fs-scroll-area" onContextMenu={e => e.preventDefault()}>
              <img
                src={fullscreenImg.url}
                alt={fullscreenImg.originalName}
                draggable={false}
                onContextMenu={e => e.preventDefault()}
                style={{ transform: `scale(${zoomLevel})`, transition: 'transform 0.2s', userSelect: 'none', pointerEvents: 'none' }}
              />
            </div>
            <div className="img-fs-info">
              <span>{fullscreenImg.originalName}</span>
              <span>{(fullscreenImg.size / 1024).toFixed(0)} KB</span>
            </div>
          </div>
        </div>
      )}

      {/* ──── Create Folder Modal ──────────────────────────── */}
      {showCreateFolder && (
        <div className="img-modal-bg" onClick={() => setShowCreateFolder(false)}>
          <form className="img-modal" onClick={e => e.stopPropagation()} onSubmit={handleCreateFolder}>
            <h3>📁 Create Folder</h3>
            <div className="img-field">
              <label>Folder Name</label>
              <input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="My Wedding Photos" required />
            </div>
            <div className="img-field">
              <label>Download Password</label>
              <input type="password" value={newFolderPassword} onChange={e => setNewFolderPassword(e.target.value)} placeholder="Required for downloading" required />
              <small>This password is required when downloading images from this folder.</small>
            </div>
            <div className="img-modal-btns">
              <button type="submit" disabled={creatingFolder}>{creatingFolder ? "Creating…" : "Create"}</button>
              <button type="button" onClick={() => setShowCreateFolder(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ──── Download Password Modal ──────────────────────── */}
      {showDownloadModal && (
        <div className="img-modal-bg" onClick={() => setShowDownloadModal(false)}>
          <form className="img-modal" onClick={e => e.stopPropagation()} onSubmit={handleFolderDownload}>
            <h3>🔒 Folder Password</h3>
            <p>Enter password to download selected images.</p>
            <div className="img-field">
              <input type="password" value={downloadPassword} onChange={e => setDownloadPassword(e.target.value)} placeholder="Folder password" required autoFocus />
            </div>
            <div className="img-modal-btns">
              <button type="submit" disabled={downloading}>
                {downloading ? (downloadProgress || "Downloading…") : "Download"}
              </button>
              <button type="button" onClick={() => setShowDownloadModal(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Images;
